import React from 'react'

import { IconName } from 'components_new/atoms/Icon'

import { DashboardStatus } from 'types/GlobalDashboardGroup'
import { Category } from 'types/GlobalDashboardGroup'
import {
  BreakdownByDisplayType,
  FunnelStatus,
  PieChartValueVisualizationType,
  TextAlign,
  TextVerticalAlign,
  WidgetType
} from 'types/GlobalWidget'
import { ChipProps } from 'components_new/atoms/Chip'
import { Color } from 'themes'

import {
  ComparativeDisplayType,
  Condition,
  RollingType
} from 'types/GlobalKpiOption'
import { MetricStatus } from 'types/GlobalMetric'

import { Severity } from './types'
import { TFunction, TranslateEnumFunction } from 'language/translator'

export const translateComparativeDisplayType: Record<
  ComparativeDisplayType,
  { iconName: IconName }
> = {
  [ComparativeDisplayType.PERCENTAGE]: {
    iconName: 'PercentOutlined'
  },
  [ComparativeDisplayType.VALUE]: {
    iconName: 'AlignVerticalBottomOutlined'
  },
  [ComparativeDisplayType.PERCENTAGE_AND_VALUE]: {
    iconName: 'StackedBarChartOutlined'
  },
  [ComparativeDisplayType.PREVIOUS_VALUE]: {
    iconName: 'HistoryOutlined'
  },
  [ComparativeDisplayType.HIDDEN]: {
    iconName: 'VisibilityOffOutlined'
  }
}

export const translateWidgetType: Record<
  WidgetType,
  {
    iconName: IconName
  }
> = {
  [WidgetType.KEY_FIGURE]: {
    iconName: 'OneKOutlined'
  },
  [WidgetType.BAR_CHART]: {
    iconName: 'BarChartOutlined'
  },
  [WidgetType.BUBBLE_CHART]: {
    iconName: 'BubbleChartOutlined'
  },
  [WidgetType.LINE_CHART]: {
    iconName: 'ShowChartOutlined'
  },
  [WidgetType.LIST]: {
    iconName: 'List'
  },
  [WidgetType.MAP]: {
    iconName: 'MapOutlined'
  },
  [WidgetType.COMBO_CHART]: {
    iconName: 'AddchartOutlined'
  },
  [WidgetType.PIE_CHART]: {
    iconName: 'PieChartOutlined'
  },
  [WidgetType.FUNNEL]: {
    iconName: 'CalendarViewWeekOutlined'
  },
  [WidgetType.TEXT]: {
    iconName: 'TitleOutlined'
  },
  [WidgetType.COMMENT]: {
    iconName: 'StickyNote2Outlined'
  },
  [WidgetType.IMAGE]: {
    iconName: 'ImageOutlined'
  },
  [WidgetType.RICH_TEXT]: {
    iconName: 'TitleOutlined' // $TODO
  }
}

export const translateFilterCondition: Record<
  Condition,
  { color: Severity; iconName: IconName; textIcon: string }
> = {
  [Condition.IS_NULL]: {
    color: 'error',
    iconName: 'CheckBoxOutlineBlankOutlined',
    textIcon: '☐'
  },
  [Condition.EQ]: {
    color: 'success',
    iconName: 'CheckCircleOutlined',
    textIcon: '='
  },
  [Condition.NE]: {
    color: 'error',
    iconName: 'Unpublished',
    textIcon: '≠'
  },
  [Condition.LTE]: {
    color: 'info',
    iconName: 'ArrowUpwardOutlined', // change?
    textIcon: '≤'
  },
  [Condition.LT]: {
    color: 'info',
    iconName: 'ArrowUpwardOutlined',
    textIcon: '<'
  },
  [Condition.GTE]: {
    color: 'info',
    iconName: 'ArrowUpwardOutlined', // change?
    textIcon: '≥'
  },
  [Condition.GT]: {
    color: 'info',
    iconName: 'ArrowUpwardOutlined',
    textIcon: '>'
  },
  [Condition.IS_NOT_NULL]: {
    color: 'success',
    iconName: 'CheckBoxOutlined',
    textIcon: '☑'
  }
}

export const translateFilter = (
  leftAttribute: string,
  condition: Condition,
  value: string | number,
  t: TFunction,
  translateEnum: TranslateEnumFunction,
  fromValue?: string | null,
  toValue?: string | null
) => {
  if (fromValue && toValue) {
    return (
      <>
        <b>{leftAttribute}</b> {t('global', 'from')} <b>{fromValue}</b>{' '}
        {t('global', 'to')} <b>{toValue}</b>{' '}
        {translateEnum('conditions', condition)} <b>{value}</b>
      </>
    )
  }

  return (
    <>
      <b>{leftAttribute}</b> {translateEnum('conditions', condition)}{' '}
      <b>{value}</b>
    </>
  )
}

export const translateDashboardStatus: Record<
  DashboardStatus,
  {
    color: ChipProps['color']
  }
> = {
  [DashboardStatus.DRAFT]: {
    color: 'info'
  },
  [DashboardStatus.PUBLISHED]: {
    color: 'success'
  },
  [DashboardStatus.OPEN]: {
    color: 'success'
  },
  [DashboardStatus.COMPLETED]: {
    color: 'default'
  }
}

export const translateCategory: Record<
  Category,
  {
    color: Color
    icon: IconName
  }
> = {
  [Category.ENERGY]: {
    color: 'quintary',
    icon: 'EnergySavingsLeaf'
  },
  [Category.RENTAL]: {
    color: 'secondary',
    icon: 'AddBusiness'
  },
  [Category.REAL_ESTATE_PORTFOLIO]: {
    color: 'senary',
    icon: 'Business'
  },
  [Category.TECHNICAL_MANAGEMENT]: {
    color: 'tertiary',
    icon: 'Construction'
  },
  [Category.ECONOMY]: {
    color: 'primary',
    icon: 'AccountBalance'
  },
  [Category.HOMEPAL]: {
    color: 'primary',
    icon: 'AccountBalance'
  }
}

export const translateFunnelStatusType: Record<
  FunnelStatus,
  {
    iconName: IconName
    color: 'error' | 'success'
  } | null
> = {
  [FunnelStatus.TERMINATED]: {
    iconName: 'CancelTwoTone',
    color: 'error'
  },
  [FunnelStatus.UNSET]: null,
  [FunnelStatus.COMPLETED]: {
    iconName: 'CheckCircleTwoTone',
    color: 'success'
  }
}

export const translateStatus: Record<
  string,
  {
    colorName: 'error' | 'success' | 'warning'
    iconName: IconName
  }
> = {
  error: {
    colorName: 'error',
    iconName: 'Report'
  },
  success: {
    colorName: 'success',
    iconName: 'CheckCircle'
  },
  warning: {
    colorName: 'warning',
    iconName: 'Error'
  }
}

export const TRANSLATE_ROLLING: Record<RollingType, { iconName: IconName }> = {
  [RollingType.R7]: {
    iconName: 'CalendarMonthOutlined'
  },
  [RollingType.R30]: {
    iconName: 'CalendarMonthOutlined'
  },
  [RollingType.R90]: {
    iconName: 'CalendarMonthOutlined'
  },
  [RollingType.R365]: {
    iconName: 'CalendarMonthOutlined'
  },
  [RollingType.R12]: {
    iconName: 'EventNoteOutlined'
  }
}

export const translateBreakdownByDisplayType: Record<
  BreakdownByDisplayType,
  {
    iconName: IconName
  }
> = {
  [BreakdownByDisplayType.CLOSE_BY]: {
    iconName: 'BarChart'
  },
  [BreakdownByDisplayType.STACKED]: {
    iconName: 'StackedBarChart'
  },
  [BreakdownByDisplayType.STACKED_100_PERCENT]: {
    iconName: 'ViewColumn'
  }
}

export const translatePieChartValueVisualizationType: Record<
  PieChartValueVisualizationType,
  { iconName: IconName }
> = {
  [PieChartValueVisualizationType.VALUES]: {
    iconName: 'LooksOneOutlined'
  },
  [PieChartValueVisualizationType.PERCENTAGE]: {
    iconName: 'PercentOutlined'
  },
  [PieChartValueVisualizationType.VALUES_AND_PERCENTAGE]: {
    iconName: 'Filter1Outlined'
  }
}

export const translateMetricStatus: Record<
  MetricStatus,
  {
    color: ChipProps['color']
    slug: string
  }
> = {
  [MetricStatus.ACTIVE]: {
    color: 'success',
    slug: 'active'
  },
  [MetricStatus.INACTIVE]: {
    color: 'default',
    slug: 'inactive'
  },
  [MetricStatus.MISSING_DATA]: {
    color: 'default',
    slug: 'nodata'
  },
  [MetricStatus.MISSING_SLICES]: {
    color: 'default',
    slug: 'slices'
  },
  [MetricStatus.MISSING_DEFINITION]: {
    color: 'default',
    slug: 'definition'
  }
}

export const translateTextAlign: Record<TextAlign, { iconName: IconName }> = {
  [TextAlign.LEFT]: {
    iconName: 'FormatAlignLeftOutlined'
  },
  [TextAlign.CENTER]: {
    iconName: 'FormatAlignCenterOutlined'
  },
  [TextAlign.RIGHT]: {
    iconName: 'FormatAlignRightOutlined'
  }
}

export const translateTextVerticalAlign: Record<
  TextVerticalAlign,
  { iconName: IconName }
> = {
  [TextVerticalAlign.TOP]: {
    iconName: 'VerticalAlignTopOutlined'
  },
  [TextVerticalAlign.CENTER]: {
    iconName: 'VerticalAlignCenterOutlined'
  },
  [TextVerticalAlign.BOTTOM]: {
    iconName: 'VerticalAlignBottomOutlined'
  }
}
